import './styles.css';

import { useState, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { syncAndRetry, checkoutPublished } from '../../git';
import { loadProjectComponents, setTailwindTheme } from '../../utility';
import { TailwindThemeHooks, useB64UrlForProjectImage } from '../..';
import { useProject } from '../../hooks';
import GlobalStyle from './GlobalStyle';
import SpinnerOverlay from '../SpinnerOverlay/SpinnerOverlay';
import { MiniAppRouter } from './Router';

const computeLayout = (components: any, layout: any[]) => {
  const sorted = layout.toSorted((a: any, b: any) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });

  const toRender: any[] = [];
  sorted.forEach((item: any) => {
    toRender.push(components.find((comp: any) => comp.id === item.i) ?? null);
  });
  return toRender;
};

const InnerContext = () => {
  const { pagePath } = useParams<{ pagePath?: string }>();
  const history = useHistory();

  const { projectMetadata } = useProject();
  const { siteBackground, setSiteBackground } = TailwindThemeHooks.useSiteBackground();
  const { siteBackgroundColor, siteBackgroundImage } = TailwindThemeHooks.useSiteBackground();

  const [rawComponents, setRawComponents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const load = async (skipSync = false) => {
    if (!skipSync) {
      setIsLoading(true);
      await syncAndRetry(projectMetadata?.projectName || '', projectMetadata?.projectURL || '');
      setIsLoading(false);
    }

    try {
      await checkoutPublished(projectMetadata?.projectName || '');
    } catch (error) {
      console.warn('Unable to checkout published tag. Ignoring', error);
    }

    const componentData = await loadProjectComponents(projectMetadata?.projectName || '', {
      pagePath,
    });

    const layoutItemIdSet = new Set(componentData?.layout?.map((layoutItem: any) => layoutItem?.i));

    const pageComponents = componentData?.components.filter((c: any) => layoutItemIdSet.has(c?.id));

    const normalized = computeLayout(pageComponents, componentData?.layout);

    setRawComponents(normalized ?? []);

    const themeFilePath = `${window.miDir}/${projectMetadata.projectName}/src/theme.json`;
    const themeContent = await window.pfs.readFile(themeFilePath, 'utf8');
    const theme = JSON.parse(themeContent as string);

    const tailwindTheme = theme.tailwind;
    const siteBackground = theme.siteBackground;
    (window as any).tailwind.config = tailwindTheme;

    setTailwindTheme(tailwindTheme);
    setSiteBackground(siteBackground);
  };

  const backgroundImageUrl = useB64UrlForProjectImage(
    projectMetadata?.projectName || '',
    siteBackgroundImage
  );
  const siteBackgroundStyles = useMemo(() => {
    // if by some chance BOTH are set (which they never should be), prioritize the image
    if (siteBackgroundImage) {
      return {
        backgroundImage: siteBackgroundImage ? `url(${backgroundImageUrl})` : '',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      };
    }

    if (siteBackgroundColor) {
      return {
        background: siteBackgroundColor ? siteBackgroundColor : '',
      };
    }

    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteBackground, siteBackgroundImage, siteBackgroundColor, backgroundImageUrl]);
  const loadBackground = async () => {
    Object.assign(document.documentElement.style, {
      minHeight: '100%',
    });

    Object.assign(document.body.style, {
      minHeight: '100%',
      margin: '0',
      padding: '0',
      ...siteBackgroundStyles,
    });
  };

  const renderComponents = useMemo(() => {
    return rawComponents.map((c: any) => <div className="render-component">{c.component}</div>);
  }, [rawComponents]);

  useEffect(() => {
    if (!projectMetadata.projectName && !projectMetadata.projectURL) return;

    load(isRedirecting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectMetadata.projectName, projectMetadata.projectURL, pagePath]);

  useEffect(() => {
    loadBackground();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteBackgroundStyles]);

  useEffect(() => {
    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Find the closest anchor tag (in case the click is on a child element)
      const anchor = target.closest('a');

      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href) {
        return;
      }

      // Allow-list: do NOT intercept clicks for:
      // - target=_blank or download links
      // - non-http(s) schemes (mailto:, tel:, sms:, javascript:)
      // - external origins
      // - server-handled paths: /oauth/*, /bff/*, /direct-render-zip/*
      const targetAttr = anchor.getAttribute('target');
      if (targetAttr === '_blank' || anchor.hasAttribute('download')) {
        return;
      }
      if (
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('sms:') ||
        href.startsWith('javascript:')
      ) {
        return;
      }
      try {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) {
          return; // external link
        }
        if (
          url.pathname.startsWith('/oauth/') ||
          url.pathname.startsWith('/bff/') ||
          url.pathname.startsWith('/direct-render-zip/')
        ) {
          return; // let the server handle these
        }
      } catch {
        // If parsing fails, let browser handle it
        return;
      }

      // Internal SPA navigation: prevent default and route client-side
      event.preventDefault();
      setIsRedirecting(true);
      history.push(href);
    };

    window.addEventListener('click', handleLinkClick, true);

    return () => {
      window.removeEventListener('click', handleLinkClick, true);
    };
  }, [history]);

  return (
    <div className="render-context">
      <GlobalStyle />
      <SpinnerOverlay isVisible={isLoading} />
      <div className="render-container">{renderComponents}</div>
    </div>
  );
};

export const MiniAppRenderContext = () => {
  return (
    <>
      <MiniAppRouter>
        <InnerContext />
      </MiniAppRouter>
    </>
  );
};
